const UNAVAILABLE_STATES = new Set(["laundry", "maintenance", "closed"]);

const minutes = (hour, minute, meridiem) => {
  let h = Number(hour);
  const m = Number(minute || 0);
  if (meridiem) {
    const part = meridiem.toUpperCase();
    if (part === "PM" && h !== 12) h += 12;
    if (part === "AM" && h === 12) h = 0;
  }
  return h * 60 + m;
};

export function parseJourneyShift(value = "") {
  const text = String(value).replace(/[–—]/g, "-").trim();
  const matches = [...text.matchAll(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/gi)];
  if (matches.length < 2) return null;
  let start = minutes(matches[0][1], matches[0][2], matches[0][3]);
  let end = minutes(matches[1][1], matches[1][2], matches[1][3]);
  if (!matches[0][3] && matches[1][3]) {
    const endMeridiem = matches[1][3].toUpperCase();
    if (endMeridiem === "PM" && start < 12 * 60 && end > 12 * 60) {
      // A schedule such as 9:00-5:00 PM means 9:00 AM, not 9:00 PM.
    } else if (endMeridiem === "AM" && start >= 12 * 60) {
      start -= 12 * 60;
    }
  }
  if (end <= start) end += 24 * 60;
  return { start, end, text: String(value).trim() };
}

export function journeyShiftPhase(shift) {
  const parsed = typeof shift === "string" ? parseJourneyShift(shift) : shift;
  if (!parsed) return "";
  // Closing is determined by when the shift finishes, not when it begins.
  // This keeps a 3:00–9:00 PM therapist out of the Mid bucket.
  if (parsed.end >= 20 * 60) return "Closing";
  if (parsed.start < 12 * 60) return "Opening";
  return "Mid";
}

export function journeyShiftsOverlap(a, b) {
  const first = typeof a === "string" ? parseJourneyShift(a) : a;
  const second = typeof b === "string" ? parseJourneyShift(b) : b;
  if (!first || !second) return true;
  return first.start < second.end && second.start < first.end;
}

const assignmentFromLegacy = (room) => {
  if (!room?.mt_id) return [];
  return [{
    employee_id: room.mt_id,
    employee_name: room.mt_name || "",
    preferred_name: room.preferred_name || "",
    shift: room.shift || "",
    phase: journeyShiftPhase(room.shift),
    source: room.assignment_source || "manual",
  }];
};

export function roomAssignments(room = {}) {
  const rows = Array.isArray(room.assignments) ? room.assignments : assignmentFromLegacy(room);
  return rows.filter((row) => row?.employee_id).map((row) => ({
    ...row,
    phase: row.phase || journeyShiftPhase(row.shift),
    source: row.source || "manual",
  }));
}

const physicalRoomNumbers = (roomOrder) => (roomOrder?.length ? roomOrder : Array.from({ length: 11 }, (_, index) => index + 1)).map(Number);

export function autoAssignJourneyRooms({ therapists = [], preferences = [], currentRooms = {}, roomOrder }) {
  const order = physicalRoomNumbers(roomOrder);
  const nextRooms = {};
  const preserved = [];
  const unavailable = new Set();

  for (const roomNumber of order) {
    const key = String(roomNumber);
    const existing = { ...(currentRooms[key] || {}) };
    if (UNAVAILABLE_STATES.has(existing.state)) unavailable.add(roomNumber);
    const manual = roomAssignments(existing).filter((assignment) => assignment.source !== "auto");
    preserved.push(...manual.map((assignment) => ({ ...assignment, room: roomNumber })));
    nextRooms[key] = {
      ...existing,
      assignments: manual,
      mt_id: manual[0]?.employee_id || null,
      mt_name: manual[0]?.employee_name || null,
      shift: manual[0]?.shift || null,
      assignment_source: manual[0] ? "manual" : null,
    };
    if (!manual.length && !UNAVAILABLE_STATES.has(existing.state)) nextRooms[key].state = "available";
  }

  const alreadyPlaced = new Set(preserved.map((assignment) => assignment.employee_id));
  const prefMap = new Map(preferences.map((preference) => [preference.employee_id, preference.preferred_rooms || []]));
  const candidates = therapists
    .map((therapist) => ({ ...therapist, parsed: parseJourneyShift(therapist.shift) }))
    .filter((therapist) => therapist.employee_id && therapist.parsed && !alreadyPlaced.has(therapist.employee_id))
    .sort((a, b) => a.parsed.start - b.parsed.start || a.parsed.end - b.parsed.end || String(a.employee_name).localeCompare(String(b.employee_name)));

  let preferencesHonored = 0;
  const unplaced = [];

  for (const therapist of candidates) {
    const preferred = prefMap.get(therapist.employee_id) || [];
    const choices = [...new Set([...preferred, ...order])].filter((roomNumber) => order.includes(Number(roomNumber)));
    const roomNumber = choices.find((candidate) => {
      const number = Number(candidate);
      if (unavailable.has(number)) return false;
      return roomAssignments(nextRooms[String(number)]).every((assignment) => !journeyShiftsOverlap(assignment.shift, therapist.shift));
    });

    if (!roomNumber) {
      unplaced.push({ employee_id: therapist.employee_id, employee_name: therapist.employee_name, shift: therapist.shift });
      continue;
    }

    const number = Number(roomNumber);
    const key = String(number);
    const assignment = {
      employee_id: therapist.employee_id,
      employee_name: therapist.employee_name,
      preferred_name: therapist.preferred_name || therapist.nickname || "",
      shift: therapist.shift,
      phase: journeyShiftPhase(therapist.parsed),
      source: "auto",
    };
    const assignments = [...roomAssignments(nextRooms[key]), assignment].sort((a, b) => (parseJourneyShift(a.shift)?.start || 0) - (parseJourneyShift(b.shift)?.start || 0));
    nextRooms[key] = {
      ...nextRooms[key],
      state: "assigned",
      assignments,
      mt_id: assignments[0].employee_id,
      mt_name: assignments[0].employee_name,
      shift: assignments[0].shift,
      assignment_source: assignments[0].source,
    };
    if (preferred.map(Number).includes(number)) preferencesHonored += 1;
  }

  return {
    rooms: nextRooms,
    summary: {
      assigned: candidates.length - unplaced.length,
      preserved_manual: preserved.length,
      unavailable: unavailable.size,
      preferences_honored: preferencesHonored,
      unplaced,
    },
  };
}

export function setManualJourneyAssignment({ rooms, roomNumber, therapist }) {
  const next = { ...(rooms || {}) };
  for (const key of Object.keys(next)) {
    const assignments = roomAssignments(next[key]).filter((assignment) => assignment.employee_id !== therapist.employee_id);
    next[key] = {
      ...next[key],
      assignments,
      state: UNAVAILABLE_STATES.has(next[key].state) ? next[key].state : assignments.length ? "assigned" : "available",
      mt_id: assignments[0]?.employee_id || null,
      mt_name: assignments[0]?.employee_name || null,
      shift: assignments[0]?.shift || null,
      assignment_source: assignments[0]?.source || null,
    };
  }
  if (!roomNumber) return next;
  const key = String(roomNumber);
  if (UNAVAILABLE_STATES.has(next[key]?.state)) return rooms;
  const conflicts = roomAssignments(next[key]).some((assignment) => journeyShiftsOverlap(assignment.shift, therapist.shift));
  if (conflicts) return rooms;
  const assignment = {
    employee_id: therapist.employee_id,
    employee_name: therapist.employee_name,
    preferred_name: therapist.preferred_name || therapist.nickname || "",
    shift: therapist.shift,
    phase: therapist.journey_phase || journeyShiftPhase(therapist.shift),
    source: "manual",
  };
  const assignments = [...roomAssignments(next[key]), assignment].sort((a, b) => (parseJourneyShift(a.shift)?.start || 0) - (parseJourneyShift(b.shift)?.start || 0));
  next[key] = {
    ...(next[key] || {}),
    state: "assigned",
    assignments,
    mt_id: assignments[0].employee_id,
    mt_name: assignments[0].employee_name,
    shift: assignments[0].shift,
    assignment_source: assignments[0].source,
  };
  return next;
}
